import { useDispatch } from "react-redux";
import { useChangeOrgMutation } from "../api/authApi";
import { apiSlice } from "../../../_api/apiSlice";
import { exportApi } from "../../../_api/exportApiSlice";
import { organisationChanged } from "../../../_app/organisationChanged";
import { setCredentials } from "../state/authSlice";
import { useToast } from "../../../_context/ToastContext/ToastContext";

const DEFAULT_ERROR_MESSAGE = "Failed to switch organisation. Please try again.";

/**
 * Single source of truth for "switch active org": call the API, commit the new
 * credentials, then run any caller-specific follow-up (permission refetch,
 * object-customization fetch, navigation). Callers opt into follow-up work via
 * `onSuccess` instead of re-implementing the changeOrg -> setCredentials sequence.
 */
export const useChangeOrganisation = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [changeOrgApiCall, { isLoading: isChangingOrg }] =
    useChangeOrgMutation();

  const changeOrganisation = async (orgId, { onSuccess } = {}) => {
    try {
      const res = await changeOrgApiCall({ orgId }).unwrap();
      dispatch(setCredentials(res));
      // Everything cached so far belongs to the previous organisation (query
      // arguments do not include the org). Drop it so the new org never shows
      // the old one's companies, counts or lists until a manual refresh.
      dispatch(apiSlice.util.resetApiState());
      dispatch(exportApi.util.resetApiState());
      // Persisted list filters belong to the previous organisation too.
      dispatch(organisationChanged());

      if (onSuccess) {
        await onSuccess(res);
      }

      return res;
    } catch (err) {
      showToast(err?.data?.message || DEFAULT_ERROR_MESSAGE, "error");
      throw err;
    }
  };

  return { changeOrganisation, isChangingOrg };
};
